import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  writeBatch,
  onSnapshot,
  QueryConstraint,
  DocumentSnapshot,
  FirestoreError as FirebaseFirestoreError
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import type { z } from 'zod';

/**
 * Custom error classes for Firestore operations
 */
class FirestoreError extends Error {
  public code: string;
  public operation: string;
  public collection: string;
  public originalError?: Error;

  constructor(
    message: string,
    code: string,
    operation: string,
    collection: string,
    originalError?: Error
  ) {
    super(message);
    this.name = 'FirestoreError';
    this.code = code;
    this.operation = operation;
    this.collection = collection;
    this.originalError = originalError;
  }
}

class ValidationError extends FirestoreError {
  public validationErrors: any;

  constructor(
    message: string,
    validationErrors: any,
    collection: string,
    operation: string
  ) {
    super(message, 'validation-error', operation, collection);
    this.name = 'ValidationError';
    this.validationErrors = validationErrors;
  }
}

class PermissionError extends FirestoreError {
  constructor(message: string, collection: string, operation: string) {
    super(message, 'permission-denied', operation, collection);
    this.name = 'PermissionError';
  }
}

class NotFoundError extends FirestoreError {
  constructor(id: string, collection: string) {
    super(`Document with ID ${id} not found in collection ${collection}`, 'not-found', 'get', collection);
    this.name = 'NotFoundError';
  }
}

/**
 * Data transformation utilities
 */
class DataTransformer {
  /**
   * Convert Firebase Timestamp to Date
   */
  static timestampToDate(timestamp: Timestamp): Date {
    return timestamp.toDate();
  }

  /**
   * Convert Date to Firebase Timestamp
   */
  static dateToTimestamp(date: Date): Timestamp {
    return Timestamp.fromDate(date);
  }

  /**
   * Sanitize data by removing undefined values
   */
  static sanitizeData(data: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        sanitized[key] = value;
      }
    }
    return sanitized;
  }

  /**
   * Transform document data with automatic timestamp conversion
   */
  static transformDocumentData<T>(doc: DocumentSnapshot): T | null {
    if (!doc.exists()) return null;
    
    const data = doc.data();
    const transformed: any = { ...data };
    
    // Convert Timestamp fields to Date objects for easier handling
    for (const [key, value] of Object.entries(transformed)) {
      if (value && typeof value === 'object' && 'toDate' in value && typeof (value as any).toDate === 'function') {
        transformed[key] = (value as any).toDate();
      }
    }
    
    // Always use the Firestore document ID, don't let data override it
    transformed.id = doc.id;
    
    return transformed as T;
  }
}

/**
 * Query builder for type-safe Firestore queries
 */
class QueryBuilder<T> {
  private constraints: QueryConstraint[] = [];
  private collectionName: string;

  constructor(collectionName: string) {
    this.collectionName = collectionName;
  }

  where(field: keyof T, operator: any, value: any): QueryBuilder<T> {
    this.constraints.push(where(field as string, operator, value));
    return this;
  }

  orderBy(field: keyof T, direction: 'asc' | 'desc' = 'asc'): QueryBuilder<T> {
    this.constraints.push(orderBy(field as string, direction));
    return this;
  }

  limit(count: number): QueryBuilder<T> {
    this.constraints.push(limit(count));
    return this;
  }

  startAfter(snapshot: DocumentSnapshot): QueryBuilder<T> {
    this.constraints.push(startAfter(snapshot));
    return this;
  }

  build() {
    return query(collection(db, this.collectionName), ...this.constraints);
  }

  getConstraints(): QueryConstraint[] {
    return [...this.constraints];
  }
}

/**
 * Pagination helper for query results
 */
interface PaginationOptions {
  limit?: number;
  startAfter?: DocumentSnapshot;
}

interface PaginatedResult<T> {
  data: T[];
  hasMore: boolean;
  lastDoc?: DocumentSnapshot;
  total?: number;
}

/**
 * Audit logging interface
 */
export interface AuditContext {
  userId: string;
  userName: string;
  action: 'create' | 'update' | 'delete';
  resourceType: string;
  resourceId: string;
  changes?: Record<string, any>;
  cycleId?: string;
}

/**
 * Enhanced Firestore service class with comprehensive features
 */
export class FirestoreService<T extends { id: string }> {
  private schema?: z.ZodSchema<any>;
  private auditEnabled: boolean = false;
  private collectionName: string;

  constructor(
    collectionName: string,
    options?: {
      schema?: z.ZodSchema<any>;
      enableAudit?: boolean;
    }
  ) {
    this.collectionName = collectionName;
    this.schema = options?.schema;
    this.auditEnabled = options?.enableAudit ?? false;
  }

  /**
   * Error handling wrapper
   */
  private handleError(error: any, operation: string, id?: string): never {
    console.error(`Firestore ${operation} error in ${this.collectionName}:`, error);

    if (error instanceof FirebaseFirestoreError) {
      switch (error.code) {
        case 'permission-denied':
          throw new PermissionError(
            `Permission denied for ${operation} operation`,
            this.collectionName,
            operation
          );
        case 'not-found':
          throw new NotFoundError(id || 'unknown', this.collectionName);
        default:
          throw new FirestoreError(
            error.message,
            error.code,
            operation,
            this.collectionName,
            error
          );
      }
    }

    throw new FirestoreError(
      error.message || `Unknown error during ${operation}`,
      'unknown',
      operation,
      this.collectionName,
      error
    );
  }

  /**
   * Validate data using Zod schema if provided
   */
  private validateData(data: any, operation: string): void {
    if (!this.schema) return;

    const result = this.schema.safeParse(data);
    if (!result.success) {
      throw new ValidationError(
        `Validation failed for ${operation}`,
        result.error.issues,
        this.collectionName,
        operation
      );
    }
  }

  /**
   * Create audit log entry (placeholder for audit service integration)
   */
  private async createAuditLog(context: AuditContext): Promise<void> {
    if (!this.auditEnabled) return;

    // This will be integrated with the audit service in a later task
    console.log('Audit log:', context);
  }

  /**
   * Create a new document
   */
  async create(
    data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>,
    auditContext?: Partial<AuditContext>
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const docData = DataTransformer.sanitizeData({
        ...data,
        createdAt: now,
        updatedAt: now,
      });

      // Validate data if schema is provided
      this.validateData(docData, 'create');

      const docRef = await addDoc(collection(db, this.collectionName), docData);

      // Create audit log
      if (auditContext) {
        await this.createAuditLog({
          action: 'create',
          resourceType: this.collectionName,
          resourceId: docRef.id,
          ...auditContext,
        } as AuditContext);
      }

      return docRef.id;
    } catch (error) {
      this.handleError(error, 'create');
    }
  }

  /**
   * Get a document by ID
   */
  async get(id: string): Promise<T | null> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      return DataTransformer.transformDocumentData<T>(docSnap);
    } catch (error) {
      this.handleError(error, 'get', id);
    }
  }

  /**
   * Get a document by ID with error if not found
   */
  async getRequired(id: string): Promise<T> {
    const result = await this.get(id);
    if (!result) {
      throw new NotFoundError(id, this.collectionName);
    }
    return result;
  }

  /**
   * Update a document
   */
  async update(
    id: string,
    data: Partial<Omit<T, 'id' | 'createdAt'>>,
    auditContext?: Partial<AuditContext>
  ): Promise<void> {
    try {
      const updateData = DataTransformer.sanitizeData({
        ...data,
        updatedAt: Timestamp.now(),
      });
      // Validate data if schema is provided
      this.validateData(updateData, 'update');

      const docRef = doc(db, this.collectionName, id);
      await updateDoc(docRef, updateData);

      // Create audit log
      if (auditContext) {
        await this.createAuditLog({
          action: 'update',
          resourceType: this.collectionName,
          resourceId: id,
          changes: data,
          ...auditContext,
        } as AuditContext);
      }
    } catch (error) {
      this.handleError(error, 'update', id);
    }
  }

  /**
   * Delete a document
   */
  async delete(id: string, auditContext?: Partial<AuditContext>): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);

      // Create audit log
      if (auditContext) {
        await this.createAuditLog({
          action: 'delete',
          resourceType: this.collectionName,
          resourceId: id,
          ...auditContext,
        } as AuditContext);
      }
    } catch (error) {
      this.handleError(error, 'delete', id);
    }
  }

  /**
   * List documents with optional constraints
   */
  async list(constraints: QueryConstraint[] = []): Promise<T[]> {
    try {
      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs
        .map(doc => DataTransformer.transformDocumentData<T>(doc))
        .filter((item): item is T => item !== null);
    } catch (error) {
      this.handleError(error, 'list');
    }
  }

  /**
   * List documents with pagination
   */
  async listPaginated(
    constraints: QueryConstraint[] = [],
    pagination: PaginationOptions = {}
  ): Promise<PaginatedResult<T>> {
    try {
      const { limit: pageLimit = 25, startAfter: startAfterDoc } = pagination;
      
      const queryConstraints = [...constraints];
      if (startAfterDoc) {
        queryConstraints.push(startAfter(startAfterDoc));
      }
      queryConstraints.push(limit(pageLimit + 1)); // Get one extra to check if there are more

      const q = query(collection(db, this.collectionName), ...queryConstraints);
      const querySnapshot = await getDocs(q);
      
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageLimit;
      const data = docs
        .slice(0, pageLimit)
        .map(doc => DataTransformer.transformDocumentData<T>(doc))
        .filter((item): item is T => item !== null);

      return {
        data,
        hasMore,
        lastDoc: data.length > 0 ? docs[data.length - 1] : undefined,
      };
    } catch (error) {
      this.handleError(error, 'listPaginated');
    }
  }

  /**
   * Count documents matching constraints
   */
  async count(constraints: QueryConstraint[] = []): Promise<number> {
    try {
      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      this.handleError(error, 'count');
    }
  }

  /**
   * Check if a document exists
   */
  async exists(id: string): Promise<boolean> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      this.handleError(error, 'exists', id);
    }
  }

  /**
   * Create a query builder for this collection
   */
  query(): QueryBuilder<T> {
    return new QueryBuilder<T>(this.collectionName);
  }

  /**
   * Subscribe to real-time updates for a document
   */
  subscribeToDocument(id: string, callback: (data: T | null) => void): Unsubscribe {
    const docRef = doc(db, this.collectionName, id);
    return onSnapshot(docRef, (doc) => {
      const data = DataTransformer.transformDocumentData<T>(doc);
      callback(data);
    }, (error) => {
      console.error(`Subscription error for document ${id}:`, error);
    });
  }

  /**
   * Subscribe to real-time updates for a query
   */
  subscribeToQuery(
    constraints: QueryConstraint[],
    callback: (data: T[]) => void
  ): Unsubscribe {
    const q = query(collection(db, this.collectionName), ...constraints);
    return onSnapshot(q, (querySnapshot) => {
      const data = querySnapshot.docs
        .map(doc => DataTransformer.transformDocumentData<T>(doc))
        .filter((item): item is T => item !== null);
      callback(data);
    }, (error) => {
      console.error(`Subscription error for query:`, error);
    });
  }

  /**
   * Batch operations
   */
  createBatch() {
    return new FirestoreBatch<T>(this.collectionName, this.schema);
  }

}

/**
 * Batch operations service
 */
class FirestoreBatch<T extends { id: string }> {
  private batch = writeBatch(db);
  private operations = 0;
  private readonly MAX_OPERATIONS = 500;
  private collectionName: string;
  private schema?: z.ZodSchema<any>;

  constructor(
    collectionName: string,
    schema?: z.ZodSchema<any>
  ) {
    this.collectionName = collectionName;
    this.schema = schema;
  }

  private validateData(data: any, operation: string): void {
    if (!this.schema) return;

    const result = this.schema.safeParse(data);
    if (!result.success) {
      throw new ValidationError(
        `Validation failed for batch ${operation}`,
        result.error.issues,
        this.collectionName,
        operation
      );
    }
  }

  private checkOperationLimit(): void {
    if (this.operations >= this.MAX_OPERATIONS) {
      throw new FirestoreError(
        `Batch operation limit exceeded (${this.MAX_OPERATIONS})`,
        'batch-limit-exceeded',
        'batch',
        this.collectionName
      );
    }
  }

  create(id: string, data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): FirestoreBatch<T> {
    this.checkOperationLimit();
    
    const now = Timestamp.now();
    const docData = DataTransformer.sanitizeData({
      ...data,
      createdAt: now,
      updatedAt: now,
    });

    this.validateData(docData, 'create');

    const docRef = doc(db, this.collectionName, id);
    this.batch.set(docRef, docData);
    this.operations++;
    
    return this;
  }

  update(id: string, data: Partial<Omit<T, 'id' | 'createdAt'>>): FirestoreBatch<T> {
    this.checkOperationLimit();
    
    const updateData = DataTransformer.sanitizeData({
      ...data,
      updatedAt: Timestamp.now(),
    });

    this.validateData(updateData, 'update');

    const docRef = doc(db, this.collectionName, id);
    this.batch.update(docRef, updateData);
    this.operations++;
    
    return this;
  }

  delete(id: string): FirestoreBatch<T> {
    this.checkOperationLimit();
    
    const docRef = doc(db, this.collectionName, id);
    this.batch.delete(docRef);
    this.operations++;
    
    return this;
  }

  async commit(): Promise<void> {
    try {
      await this.batch.commit();
    } catch (error) {
      throw new FirestoreError(
        'Batch commit failed',
        'batch-commit-failed',
        'batch',
        this.collectionName,
        error as Error
      );
    }
  }

  getOperationCount(): number {
    return this.operations;
  }
}


